import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  keyframes,
  Menu,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  PurchaseOrderPartial,
  PurchaseOrderWithRelations,
  useGetPurchaseOrdersQuery,
  useLazyGetProcurementProcessesQuery,
  useLazyGetPurchaseOrdersQuery,
} from '../../../redux/vmsApi';
import {
  useGetUsersMeQuery,
} from '../../../redux/vmsApi';
import * as XLSX from 'xlsx';
import config from '../../../config';
import WarningIcon from '@mui/icons-material/Warning';
import BackOrderAndReceivedComparisonDialog from '../common/backOrderAndReceivedComparisonDialog';
import { getCurrentDate } from '../../../utils/dateUtil';
import { formatCurrency } from '../../../utils/currencyUtil';
import { useRouter } from "../../../hooks/useRouter";

const pulse =
  keyframes`
    0% {opacity: 1}
    50% {opacity: 0.7}
    100% {opacity: 1}
  `;

const DangerTableCell = styled(TableCell)(({ theme }) => ({
  animation: `${pulse} 1.5s infinite`,
  background: 'linear-gradient(45deg, #ff4d4d, #cc0000)',
  color: theme.palette.common.white,
  fontWeight: 'bold',
  padding: theme.spacing(1.5),
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

const BackOrdersList: React.FC = () => {
  const router = useRouter();
  const purchaseOrder = { itemsReceived: [], backOrderItems: [] };

  const [triggerProcurementProcess] = useLazyGetProcurementProcessesQuery();
  const [triggerPurchaseOrdersExport] = useLazyGetPurchaseOrdersQuery();
  const { data: currentUser } = useGetUsersMeQuery();
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState({
    amount: '',
    title: '',
    supplierName: '',
    itemCode: '',
    purchaseOrderNumber: '',
    fromDate: '2025-01-06',
    toDate: getCurrentDate(),
    development: '',
    project: '',
    reqNumber: '',
    backOrder: true,
    reportByEmail: false,
    reportName: '',
    currency: '',
    detailed: false,
  });

  const [openDialog, setOpenDialog] = useState<string | null>(null);
  const [openComparisonDialog, setOpenComparisonDialog] = useState(false);
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(purchaseOrder);
  const [reportByEmailEnabled, setReportByEmailEnabled] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [apiParams, setApiParams] = useState({ ...filters, skip: 0, limit: 10 });

  const { data: purchaseOrders, isLoading, isError } = useGetPurchaseOrdersQuery(apiParams);

  const handleFilterChange = (field: string, value: string | boolean) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setApiParams({ ...filters, skip: 0, limit: pageSize });
    setPage(0);
    setReportByEmailEnabled(false);
    handleFilterChange('reportName', '');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setApiParams((prev) => ({ ...prev, skip: newPage * pageSize }));
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(0);
    setApiParams((prev) => ({ ...prev, limit: newSize, skip: 0 }));
  };

  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      const exportData = await triggerPurchaseOrdersExport({
        ...apiParams,
        reportByEmail: false,
        reportName: '',
        skip: 0,
        limit: 200000,
      }).unwrap();

      const rows = (exportData ?? []).map((purchaseOrder: PurchaseOrderWithRelations) => ({
        poNumber: purchaseOrder.purchaseOrderNumber ?? '',
        reqNumber: purchaseOrder.reqNumber ?? '',
        title: purchaseOrder.title ?? '',
        development: purchaseOrder.development ?? '',
        project: purchaseOrder.project ?? '',
        supplier: purchaseOrder.quotation?.supplier?.name ?? '',
        expectedDeliveryDate: purchaseOrder.expectedDeliveryDate
          ? new Date(purchaseOrder.expectedDeliveryDate).toISOString()
          : '',
        currency: purchaseOrder.quotation?.currency ?? '',
        purchaseOrderValue: purchaseOrder.quotation?.grandTotal ?? '',
        backOrderItemsCount: purchaseOrder.backOrderItems?.length ?? 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'BackOrders');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      XLSX.writeFile(workbook, `back-orders-${timestamp}.xlsx`);
    } catch (error) {
      console.error('Failed to export back orders Excel', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDialogOpen = async (selectedPurchaseOrder: PurchaseOrderPartial, documentType: string) => {
    setOpenDialog(null);
    const procurementProcessId = selectedPurchaseOrder.procurementProcessId;
    const { data } = await triggerProcurementProcess({ procurementProcessId });

    const selectedProcess = data?.[0];
    if (!selectedProcess) {
      console.error("Procurement process not found");
      return;
    }

    const documentName =
      documentType === 'purchaseOrder'
        ? selectedProcess.purchaseOrderDocument?.name
        : documentType === 'quotation'
          ? selectedPurchaseOrder?.quotation?.attachments?.[0].name
          : selectedProcess.requisitionDocument?.name;


    if (documentName) {
      const url = `${config.CDN_URL}/procurement/${documentName}`;
      setOpenDialog(url);
    } else {
      console.error("Document name not available in process");
    }
  };

  const handleDialogClose = () => setOpenDialog(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedPurchaseOrderToOpen, setSelectedPurchaseOrderToOpen] = useState<PurchaseOrderWithRelations | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>, po: PurchaseOrderWithRelations) => {
    setAnchorEl(event.currentTarget);
    setSelectedPurchaseOrderToOpen(po);
  };

  const handleClose = () => setAnchorEl(null);

  const handleComparisonDialogOpen = (po: any) => {
    setSelectedPurchaseOrder(po);
    setOpenComparisonDialog(true);
  };

  const handleComparisonDialogClose = () => {
    setOpenComparisonDialog(false);
    setSelectedPurchaseOrder(purchaseOrder);
  };

  const handleViewProcessDetails = (procurementProcessId: string | undefined) => {
    if (procurementProcessId) router.push(`/procurement/procurementProcess/${procurementProcessId}`);
  };

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error fetching purchase orders.</div>;

  return (
    <React.Fragment>
      <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
          Back Orders
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <TextField
            label="Title"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.title}
            onChange={(e) => handleFilterChange('title', e.target.value)}
          />

          <TextField
            label="Supplier Name"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.supplierName}
            onChange={(e) => handleFilterChange('supplierName', e.target.value)}
          />

          <TextField
            label="Item Code"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.itemCode}
            onChange={(e) => handleFilterChange('itemCode', e.target.value)}
          />
          <TextField
            label="Purchase Order Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.purchaseOrderNumber}
            onChange={(e) => handleFilterChange('purchaseOrderNumber', e.target.value)}
          />
          <TextField
            label="From Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
          />
          <TextField
            label="To Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
          />

          <TextField
            label="Requisition Number"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.reqNumber}
            onChange={(e) => handleFilterChange('reqNumber', e.target.value)}
          />
          <TextField
            label="Development"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.development}
            onChange={(e) => handleFilterChange('development', e.target.value)}
          />
          <TextField
            label="Project"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
            value={filters.project}
            onChange={(e) => handleFilterChange('project', e.target.value)}
          />
          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            <InputLabel id="currency-select-label">Currency</InputLabel>
            <Select
              labelId="currency-select-label"
              value={filters.currency || ''}
              label="Currency"
              onChange={(e) => handleFilterChange('currency', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ZAR">ZAR</MenuItem>
              <MenuItem value="ZIG">ZIG</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Purchase Order Value"
            variant="outlined"
            size="small"
            fullWidth
            sx={{ maxWidth: 240, bgcolor: '#fff', borderRadius: 1 }}
            value={filters.amount}
            onChange={e => handleFilterChange('amount', e.target.value)}
          />

          <FormControl
            size="small"
            fullWidth
            sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1, mt: 1 }}
          >
            {/* <InputLabel id="detailed-select-label">Detailed?</InputLabel> */}
            <Select
              labelId="detailed-select-label"
              value={
                filters.detailed === undefined
                  ? ''
                  : filters.detailed
                    ? 'true'
                    : 'false'
              }
              onChange={(e) =>
                handleFilterChange(
                  'detailed',
                  e.target.value === ''
                    ? false
                    : e.target.value === 'true'
                )
              }
            >
              <MenuItem value="true">Detailed Report</MenuItem>
              <MenuItem value="false">Summary Report</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={reportByEmailEnabled}
                onChange={(e) => {
                  const isEnabled = e.target.checked;
                  setReportByEmailEnabled(isEnabled);
                  handleFilterChange('reportByEmail', isEnabled);
                  if (!isEnabled) {
                    handleFilterChange('reportName', '');
                  }
                }}
                color="primary"
              />
            }
            label="Generate & Email Report"
            sx={{ marginLeft: 1 }}
          />



          {reportByEmailEnabled && (
            <TextField
              label="Report Name"
              variant="outlined"
              size="small"
              fullWidth
              sx={{ maxWidth: 240, backgroundColor: '#fff', borderRadius: 1 }}
              value={filters.reportName}
              onChange={(e) => handleFilterChange('reportName', e.target.value)}
            />
          )}


        </Box>
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            variant="outlined"
            size="large"
            fullWidth
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#e3f2fd',
                borderColor: '#1565c0',
              },
            }}
          >
            Apply Filters
          </Button>
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled={isExporting}
            onClick={handleExportExcel}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
            }}
          >
            {isExporting ? 'Exporting...' : 'Export Excel (All Filtered)'}
          </Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>PO Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>REQ Number</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Development</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Project</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="left">Supplier</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="left">Expected Delivery Date</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Purchase Order Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {purchaseOrders && purchaseOrders.length > 0 ? (
              purchaseOrders.map((purchaseOrder: PurchaseOrderWithRelations, index: number) => (
                <TableRow key={purchaseOrder.id} sx={{ backgroundColor: index % 2 === 0 ? '#f0f4f7' : 'white' }}>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.purchaseOrderNumber || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.reqNumber || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.title || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.development || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.project || 'N/A'}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {purchaseOrder.quotation.supplier.name || 'N/A'}
                  </TableCell>

                  <TableCell>
                    <DangerTableCell>
                      <WarningIcon />
                      {new Date(purchaseOrder?.expectedDeliveryDate).toLocaleDateString('en-GB', {
                        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                      })}{' '}
                    </DangerTableCell>
                  </TableCell>

                  <TableCell component="th" scope="row">
                    {formatCurrency(purchaseOrder.quotation.grandTotal, purchaseOrder.quotation.currency || 'usd')}
                  </TableCell>

                  <TableCell>
                    <Button
                      variant={'contained'}
                      size="small"
                      onClick={(e) => handleOpen(e, purchaseOrder)}
                    >
                      Actions
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          elevation: 3,
                          sx: {
                            minWidth: 200,
                            borderRadius: 1,
                          },
                        }
                      }}
                    >
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrderToOpen?.procurementProcessId) {
                          handleDialogOpen(selectedPurchaseOrderToOpen, 'purchaseOrder');
                        }
                      }}>
                        View Purchase Order Document
                      </MenuItem>
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrderToOpen?.procurementProcessId) {
                          handleDialogOpen(selectedPurchaseOrderToOpen, 'quotation');
                        }
                      }}>
                        View Quotation Document
                      </MenuItem>
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrderToOpen?.procurementProcessId) {
                          handleDialogOpen(selectedPurchaseOrderToOpen, 'requisition');
                        }
                      }}>
                        View Requisition Document
                      </MenuItem>
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrderToOpen) {
                          handleComparisonDialogOpen(selectedPurchaseOrderToOpen);
                        }
                      }}>
                        View Back Order Items
                      </MenuItem>
                      <MenuItem onClick={() => {
                        handleClose();
                        if (selectedPurchaseOrderToOpen?.procurementProcessId) {
                          handleViewProcessDetails(selectedPurchaseOrderToOpen.procurementProcessId);
                        }
                      }}>
                        View Process Details
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">No matching Purchase Orders found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
        <Button
          onClick={() => handlePageChange(Math.max(page - 1, 0))}
          disabled={page === 0}
          variant="contained"
          sx={{ marginRight: 2 }}
        >
          Previous
        </Button>

        <Typography variant="body1">Page {page + 1}</Typography>

        <Button
          onClick={() => handlePageChange(page + 1)}
          disabled={purchaseOrders?.length === undefined || purchaseOrders.length < pageSize}
          variant="contained"
          sx={{ marginLeft: 2 }}
        >
          Next
        </Button>
      </Box>


      <Box mt={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
        <FormControl variant="outlined" size="small">
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            sx={{ backgroundColor: '#f0f4f7' }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Dialog open={!!openDialog} onClose={handleDialogClose} fullWidth maxWidth="md">
        <Box p={2}>
          {openDialog && (
            <iframe
              src={openDialog}
              title="Document Viewer"
              width="100%"
              height="500px"
              style={{ border: 'none' }}
            />
          )}
          <Box textAlign="right" mt={2}>
            <Button onClick={handleDialogClose} variant="contained" color="primary">
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
      <BackOrderAndReceivedComparisonDialog
        open={openComparisonDialog}
        onClose={handleComparisonDialogClose}
        itemsReceived={selectedPurchaseOrder?.itemsReceived || []}
        backOrderItems={selectedPurchaseOrder?.backOrderItems || []}
        selectedPurchaseOrder={selectedPurchaseOrder}
        currentUser={currentUser}
      />
    </React.Fragment>
  );
};

export default BackOrdersList;